import { useEffect, useState } from 'react';
import { Pin } from 'lucide-react';
import { Permission, type Message } from '@spawnwire/shared';
import { Button, EmptyState, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { attempt } from '../../lib/data';
import { can, useChannelPermissions } from '../../lib/perms';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { MessagePreview } from './MessagePreview';
import { PanelHeader } from './PanelHeader';

export function PinsPanel({ channelId }: { channelId: string }) {
  const [pins, setPins] = useState<Message[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const perms = useChannelPermissions(channelId);
  const isPrivate = useStore((s) => !s.channels[channelId]?.spaceId);
  // Refetch when any loaded message in this channel changes pinned state.
  const pinnedKey = useStore((s) => {
    const l = s.messages[channelId];
    return l ? l.ids.filter((id) => l.byId[id]?.pinned).join(',') : '';
  });
  const canUnpin = isPrivate || can(perms, Permission.MANAGE_MESSAGES);

  useEffect(() => {
    http
      .get<Message[]>(`/channels/${channelId}/pins`)
      .then(setPins)
      .catch((err) => setError(errorMessage(err)));
  }, [channelId, pinnedKey]);

  return (
    <>
      <PanelHeader title="Pinned messages" icon={<Pin className="size-5 text-faint" aria-hidden />} />
      <div className="scroll-thin min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {error ? (
          <p className="p-4 text-sm text-danger">{error}</p>
        ) : !pins ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : pins.length === 0 ? (
          <EmptyState icon={Pin} title="No pinned messages yet" body="Pin important messages from their menu so everyone can find them here." />
        ) : (
          pins.map((m) => (
            <MessagePreview
              key={m.id}
              message={m}
              actions={
                <>
                  <Button size="sm" variant="secondary" onClick={() => ui.jumpTo(channelId, m.id)}>
                    Jump
                  </Button>
                  {canUnpin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        void attempt(() => http.del(`/channels/${channelId}/pins/${m.id}`)).then(() => setPins((p) => p?.filter((x) => x.id !== m.id) ?? null))
                      }
                    >
                      Unpin
                    </Button>
                  )}
                </>
              }
            />
          ))
        )}
      </div>
    </>
  );
}
